import { ActionIcon, Badge, Group, Select, Stack, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { useCallback, useMemo, type MouseEvent } from "react";
import {
  isSelectablePanel,
  type StainSelectionContext,
} from "../../utils/controlStainSelection";
import type {
  AccessioningPanelOption,
  AccessioningPanelSelection,
} from "../../services/accessioningPanels.service";

interface PanelOrReflexSelectorProps {
  options: AccessioningPanelOption[];
  stainSelectionContext?: StainSelectionContext;
  selections: AccessioningPanelSelection[];
  isLoading: boolean;
  onAdd: (option: AccessioningPanelOption) => void;
  onRemove: (selectionKey: string) => void;
}

export default function PanelOrReflexSelector({
  options,
  stainSelectionContext = "ordinary",
  selections,
  isLoading,
  onAdd,
  onRemove,
}: PanelOrReflexSelectorProps) {
  const selectOptions = useMemo(
    () =>
      options
        .filter((option) => isSelectablePanel(option, stainSelectionContext))
        .map((option) => ({
          value: `${option.optionType}:${option.id}`,
          label: `${option.name} (${
            option.optionType === "reflex" ? "Reflex" : "Panel"
          })`,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [options, stainSelectionContext]
  );
  const selectionsWithOptions = useMemo(
    () =>
      selections.map((selection) => ({
        selection,
        option: options.find(
          (candidate) =>
            candidate.id === selection.optionId &&
            candidate.optionType === selection.optionType
        ),
      })),
    [options, selections]
  );
  const handleSelect = useCallback(
    (value: string | null) => {
      if (!value) return;
      const [optionType, optionId] = value.split(":");
      const option = options.find(
        (candidate) =>
          candidate.id === optionId && candidate.optionType === optionType
      );
      if (option && isSelectablePanel(option, stainSelectionContext))
        onAdd(option);
    },
    [onAdd, options, stainSelectionContext]
  );
  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const selectionKey = event.currentTarget.dataset.selectionKey;
      if (selectionKey) onRemove(selectionKey);
    },
    [onRemove]
  );

  return (
    <Stack gap="xs">
      <Select
        label="Panel or Reflex"
        placeholder={
          isLoading ? "Loading options..." : "Select a panel or reflex"
        }
        data={selectOptions}
        value={null}
        onChange={handleSelect}
        disabled={isLoading}
        searchable
        nothingFoundMessage="No active panels or reflexes found"
        comboboxProps={{ withinPortal: true, position: "bottom-start" }}
      />
      {selectionsWithOptions.map(({ selection, option }) => (
        <Group
          key={selection.selectionKey}
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap="xs" wrap="nowrap">
            <Badge
              color={selection.optionType === "reflex" ? "violet" : "blue"}
            >
              {selection.optionType === "reflex" ? "Reflex" : "Panel"}
            </Badge>
            <Text size="sm">{option?.name || "Selected panel"}</Text>
          </Group>
          <ActionIcon
            variant="subtle"
            color="red"
            size={44}
            aria-label={`Remove ${option?.name || "panel"}`}
            data-selection-key={selection.selectionKey}
            onClick={handleRemove}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      ))}
    </Stack>
  );
}
